/** Email plugin: status, the shop's mail servers, templates, sending and the email history. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';

export type EmailStatus = Schemas['EmailStatus'];
export type EmailServer = Schemas['EmailServer'];
export type EmailMessage = Schemas['EmailMessage'];
export type SendEmail = Schemas['SendEmailRequest'];
export type EmailDocumentType = NonNullable<SendEmail['documentType']>;
export type MailSecurity = EmailServer['security'];

export const emailKeys = {
  status: ['email'] as const,
  messages: (params: { documentId?: string; contactId?: string }) =>
    ['email', 'messages', params] as const,
};

export function useEmailStatus() {
  return useQuery({
    queryKey: emailKeys.status,
    queryFn: () => unwrap(api.GET('/email')),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Whether to show Email at all (plugin enabled for the shop and switched on by it), whether this
 * user may send, and which server emails each kind of document (none: no Email button on it).
 */
export function useEmail() {
  const { can } = useAuth();
  const status = useEmailStatus();
  const data = status.data;
  const plugin = data?.plugin;
  const visible = !!plugin && plugin.state !== 'NOT_ENABLED' && plugin.shopEnabled;
  return {
    status: data,
    visible,
    canSend: visible && can('email.send'),
    expired: plugin?.state === 'EXPIRED',
    /** The server that emails this kind of document; null: no Email button on it. */
    sender: (type: EmailDocumentType) => data?.documents[type] ?? null,
    /** The subject and message this kind of document starts with. */
    template: (type: EmailDocumentType) => data?.templates[type] ?? { subject: '', body: '' },
  };
}

export function useEmailMessages(
  params: { documentType?: EmailDocumentType; documentId?: string; contactId?: string },
  enabled = true,
) {
  return useQuery({
    queryKey: emailKeys.messages(params),
    queryFn: () =>
      unwrap(api.GET('/email/messages', { params: { query: { ...params, limit: 50 } } })),
    enabled: enabled && !!(params.documentId ?? params.contactId),
  });
}

export function useSendEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SendEmail) => unwrap(api.POST('/email/messages', { body })),
    onSettled: () => qc.invalidateQueries({ queryKey: ['email', 'messages'] }),
  });
}

export function useEmailAdmin() {
  const qc = useQueryClient();
  const set = (data: EmailStatus) => qc.setQueryData(emailKeys.status, data);
  return {
    settings: useMutation({
      mutationFn: (body: Schemas['UpdateEmailSettingsRequest']) =>
        unwrap(api.PATCH('/email/settings', { body })),
      onSuccess: set,
    }),
    create: useMutation({
      mutationFn: (body: Schemas['CreateEmailServerRequest']) =>
        unwrap(api.POST('/email/servers', { body })),
      onSuccess: set,
    }),
    update: useMutation({
      mutationFn: ({ id, ...body }: Schemas['UpdateEmailServerRequest'] & { id: string }) =>
        unwrap(api.PATCH('/email/servers/{id}', { params: { path: { id } }, body })),
      onSuccess: set,
    }),
    remove: useMutation({
      mutationFn: (id: string) =>
        unwrap(api.DELETE('/email/servers/{id}', { params: { path: { id } } })),
      onSuccess: set,
    }),
    test: useMutation({
      mutationFn: ({ id, to }: { id: string; to: string }) =>
        unwrap(api.POST('/email/servers/{id}/test', { params: { path: { id } }, body: { to } })),
      onSettled: () => qc.invalidateQueries({ queryKey: emailKeys.status }),
    }),
  };
}
